<template>
  <v-row v-if="isLoading" class="fill-height" justify="center" align="center">
    <v-progress-circular indeterminate color="primary" />
  </v-row>
  <div v-else class="pageContainer fill-height flex-grow-1">
    <div class="column leftcol">
      <AnalysisDetailsHeader
        :analysis="currentAnalysis"
        :analysis-id="analysisId"
        :document-id="documentId"
        class="header"
      />
      <v-card class="summary-mobile">
        <AnalysisInfo
          :analysis="analysis"
          :analysis-id="analysisId"
          @selectAnalysis="selectAnalysis"
        />
      </v-card>
      <PdfViewer
        v-if="currentAnalysis && currentAnalysis.documentDetails.isUpload"
        class="document pa-3 ma-3"
        :full-analysis="currentAnalysis"
        :width="pdfViewerWidth"
      />
      <div v-else style="height:100%;">
        <iframe
          class="document iframe ma-3 pr-6"
          :src="currentAnalysis.documentDetails.contentUrl"
        />
      </div>
    </div>
    <div class="column rightcol">
      <v-card class="summary-desktop">
        <AnalysisInfo
          :analysis="analysis"
          :analysis-id="analysisId"
          @selectAnalysis="selectAnalysis"
        />
      </v-card>
      <div class="userAnalyses mb-1">
        <v-card
          v-for="analysis in usersAnalyses"
          :key="analysis.id"
          class="d-flex mb-3"
          style="width: 100%"
        >
          <AnalysisCardOld
            :user-analysis="analysis"
            :analysis-id="currentAnalysis.id"
            class="d-flex flex-grow-1"
          />
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { mapGetters, mapActions } from 'vuex';
import { getDefaultUserAnalysis } from '@/shared/store/analysisHelper';
import AnalysisCardOld from '@/shared/components/analysis/AnalysisCardOld';
import AnalysisInfo from '@/shared/components/analysis/AnalysisInfo';
import { types } from '../../store/rootTypes';
import AnalysisDetailsHeader from './AnalysisDetails/AnalysisDetailsHeader';
import PdfViewer from './AnalysisDetails/PdfViewer';

export default {
  name: 'AnalysisDetails',
  components: {
    AnalysisDetailsHeader,
    AnalysisInfo,
    AnalysisCardOld,
    PdfViewer,
  },
  data: () => ({
    isLoading: true,
    userAnalysisCopy: undefined,
    usersAnalyses: [],
    analysis: {},
    pdfViewerWidth: 400,
  }),
  props: {
    analysisId: {
      type: String,
      default: '',
    },
    documentId: {
      type: String,
      required: true,
    },
  },
  created() {
    this.$store.commit(types.analyses.mutations.UNSET_CURRENT_ANALYSIS);
    window.addEventListener('resize', this.handleWindowResize);
    this.handleWindowResize();
  },
  destroyed() {
    window.removeEventListener('resize', this.handleWindowResize);
  },
  mounted() {
    this.loadCurrentAnalysis();
  },
  computed: {
    ...mapGetters({
      userDetails: types.user.getters.userDetails,
      currentAnalysis: types.analyses.getters.currentAnalysis,
      currentAnalysisUserAnalyses: types.analyses.getters.currentAnalysisUserAnalyses,
      preparedAnalysis: types.analysesCapture.getters.preparedAnalysis,
    }),
    currentUserAnalysis() {
      if (this.userAnalysisCopy !== undefined) {
        return this.userAnalysisCopy;
      }
      return this.currentAnalysisUserAnalyses.find(userAnalysis => userAnalysis.id === this.userDetails.userId) || null;
    },
    currentUserAnalysisIndex() {
      return this.currentAnalysisUserAnalyses.findIndex(userAnalysis => userAnalysis.id === this.userDetails.userId);
    },
    otherUserAnalysisIndex() {
      return this.currentAnalysisUserAnalyses.findIndex(userAnalysis => userAnalysis.id !== this.userDetails.userId);
    },
  },
  methods: {
    ...mapActions({
      prepareAnalysis: types.analysesCapture.actions.prepareAnalysis,
    }),
    async selectAnalysis(analysis) {
      this.isLoading = true;
      if (Object.keys(analysis).length === 0) {
        await this.prepareAnalysis(this.analysis.documentDetails);
        this.currentAnalysisCopy = cloneDeep(this.preparedAnalysis);
        this.currentAnalysisCopy.documentId = this.preparedAnalysis.documentDetails.documentId;
        const defaultUserAnalysis = getDefaultUserAnalysis({
          userId: this.userDetails.userId,
          displayName: this.userDetails.displayName,
        });
        this.usersAnalyses = [defaultUserAnalysis];

        this.isLoading = false;
        return;
      }
      const newRoute = `/doc/${analysis.documentId}/${analysis.id}`;
      if (newRoute !== this.$route.path) {
        this.$router.push(newRoute);
      }
    },
    updateUsersAnalyses() {
      if (!this.currentAnalysisUserAnalyses) return;

      this.usersAnalyses = cloneDeep(this.currentAnalysisUserAnalyses);
      this.usersAnalyses.splice(this.currentUserAnalysisIndex, 1);

      const currentUserAnalysis = cloneDeep(this.currentAnalysisUserAnalyses[this.currentUserAnalysisIndex]);
      this.usersAnalyses.splice(0, 0, currentUserAnalysis);
    },
    async loadCurrentAnalysis() {
      if (this.analysisId) {
        await this.$store.dispatch(types.analyses.actions.fetchAnalysis, this.analysisId);
      } else {
        this.$store.dispatch(types.analyses.actions.fetchAnalysesForDocument, this.documentId);
      }
    },
    handleWindowResize() {
      const windowWidth = window.innerWidth;

      if (windowWidth < 1250) {
        this.pdfViewerWidth = windowWidth - 86;
        return;
      }

      this.pdfViewerWidth = windowWidth - 652;
    },
  },
  watch: {
    currentAnalysis(analysis) {
      if (this.isLoading && this.currentAnalysisUserAnalyses !== null && this.currentAnalysisUserAnalyses.length > 0) {
        this.isLoading = false;
        this.analysis = analysis;
      }

      if (this.currentAnalysisUserAnalyses !== null) this.updateUsersAnalyses();
    },
  },
};
</script>

<style scoped>
.pageContainer {
  display: grid;
  overflow-x: hidden !important;
  grid-template-areas:
    'header'
    'document'
    'userAnalyses';
}

.summary-mobile {
  display: block;
  margin-left: 12px;
  margin-right: 12px;
  padding-right: 0px;
}

.summary-desktop {
  display: none;
  margin: 12px 12px 12px 0px;
}

.header {
  grid-area: header;
  padding: 12px;
  margin: 12px;
}

.document {
  grid-area: document;
  align-content: center;
}

.iframe {
  width: 100%;
  margin: 12px;
  height: 50vh;
  max-height: calc(90vh - 48px);
  border: none;
}

.userAnalyses {
  grid-area: userAnalyses;
  margin: 0px 12px 12px;
}

@media(min-width:1250px) {
  .pageContainer {
    display: flex;
    flex-direction: row;
  }

  .header {
    margin-bottom: 0px;
  }

  .summary-mobile {
    display: none;
  }

  .summary-desktop {
    display: block;
  }

  .iframe-card {
    height: calc(100% - 24px);
  }

  .column {
    display: flex;
    flex-direction: column;
  }

  .column.leftcol {
    min-width: 660px;
    height: auto;
    flex-grow: 0;
  }

  .column.rightcol {
    min-width: 530px;
    height: fit-content;
    flex-grow: 1;
    padding-bottom: 8px;
  }

  .userAnalyses {
    margin-left: 0px
  }
}
</style>
